import styled, { css } from 'styled-components';
import { Typography } from 'src/typography';

// Common styles
const FLEX_CENTER = `
  display: flex;
  align-items: center;
`;

const PSEUDO_STYLES = `
  content: '';
  position: absolute;
  z-index: 1;
  pointer-events: none; // Ensures the content is clickable
`;
const BLUE_BORDER = '1px solid #5a95da';
const RADIUS = '8px';
const NO_FIRST_RADIUS = `0 ${RADIUS} ${RADIUS} ${RADIUS}`;
const NO_SECOND_RADIUS = `${RADIUS} 0 ${RADIUS} ${RADIUS}`;
const ONLY_FIRST_RADIUS = `${RADIUS} 0 0 0`;
const ONLY_SECOND_RADIUS = `0 ${RADIUS} 0 0`;
const ONLY_THIRD_RADIUS = `0 0 ${RADIUS} 0`;
const ONLY_FOURTH_RADIUS = `0 0 0 ${RADIUS}`;

export const TabHeader = styled.div<{ firstTabSelected: boolean }>`
  ${({ firstTabSelected }) => css`
    display: flex;
    align-items: center;
    align-self: stretch;
    position: relative;
    &::before {
      ${PSEUDO_STYLES}
      top: -1px;
      left: ${firstTabSelected ? '-1px' : `calc(50% + ${RADIUS})`};
      right: ${firstTabSelected ? `calc(50% + ${RADIUS})` : '-1px'};
      height: 65px;
      border-top: ${BLUE_BORDER};
      border-left: ${firstTabSelected ? BLUE_BORDER : 'none'};
      border-right: ${firstTabSelected ? 'none' : BLUE_BORDER};
      border-radius: ${firstTabSelected ? ONLY_FIRST_RADIUS : ONLY_SECOND_RADIUS};
  `}
`;
export const TabBackground = styled.div<{ selected: boolean; isFirst: boolean }>`
  ${({ selected, isFirst, theme: { colors } }) => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    background-color: ${selected ? colors.white : '#f5f5f6'};
    border-radius: ${isFirst ? ONLY_FIRST_RADIUS : ONLY_SECOND_RADIUS};
    transition: all 300ms ease;

    &::before,
    &::after {
      ${PSEUDO_STYLES};
      height: 56px;
    }

    &::before {
      top: -1px;
      right: ${isFirst ? 'calc(50%)' : RADIUS};
      left: ${isFirst ? RADIUS : 'calc(50% - 1px)'};
      border-top: ${selected ? BLUE_BORDER : 'none'};
      border-right: ${isFirst && selected ? BLUE_BORDER : 'none'};
      border-left: ${!isFirst && selected ? BLUE_BORDER : 'none'};
      border-radius: ${isFirst ? ONLY_SECOND_RADIUS : ONLY_FIRST_RADIUS};
    }

    &::after {
      top: ${RADIUS};
      left: ${isFirst ? 'calc(50% - 1px)' : RADIUS};
      right: ${isFirst ? RADIUS : 'calc(50%)'};
      border-bottom: ${selected ? BLUE_BORDER : 'none'};
      border-left: ${isFirst && selected ? BLUE_BORDER : 'none'};
      border-right: ${!isFirst && selected ? BLUE_BORDER : 'none'};
      border-radius: ${isFirst ? ONLY_FOURTH_RADIUS : ONLY_THIRD_RADIUS};
    }

    &:hover ~ & {
      background-color: #e3e7e9;
    }

    &:has(~ &:hover) {
      background-color: #e3e7e9;
    }
  `}
`;

export const Tab = styled.div<{ selected: boolean; $borderRadius: string }>`
  ${({ theme: { spaces, colors }, selected, $borderRadius }) => css`
    display: flex;
    min-height: 64px;
    padding: ${spaces[3]} ${spaces[5]};
    align-items: center;
    align-content: center;
    gap: ${spaces[2]};
    flex: 1 0 0;
    flex-wrap: wrap;
    background: ${selected ? '#f5f5f6' : colors.white};
    border-radius: ${$borderRadius};
    position: relative;
    cursor: pointer;
    transition: all 300ms ease;

    &:hover {
      background: ${selected ? '#f5f5f6' : '#e3e7e9'};
    }
  `}
`;

export const TabText = styled<{ selected: boolean }>(Typography.Body)`
  line-height: 20px;
  color: ${({ theme: { colors }, selected }) => (selected ? colors.grey[25] : colors.grey[50])};
`;

export const TabHeaderContent = styled.div`
  ${FLEX_CENTER}
  flex-direction: column;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-items: flex-start;
`;

export const TabTextGroup = styled.div<{ gap?: number }>`
  ${FLEX_CENTER}
  gap: ${({ theme: { spaces }, gap }) => spaces[gap ?? 1]};
  flex-shrink: 0;
`;

export const TabContent = styled.div<{ isFirst: boolean }>`
  ${({ theme: { spaces }, isFirst }) => css`
    display: flex;
    padding: ${spaces[5]};
    flex-direction: column;
    align-items: center;
    gap: ${spaces[4]};
    align-self: stretch;
    background: #f5f5f6;
    border-radius: ${isFirst ? NO_FIRST_RADIUS : NO_SECOND_RADIUS};
    position: relative;

    &::before,
    &::after {
      ${PSEUDO_STYLES};
      top: 0;
      border-radius: inherit;
    }

    &::before {
      border-left: ${BLUE_BORDER};
      border-right: ${BLUE_BORDER};
      border-bottom: ${BLUE_BORDER};
      right: -1px;
      height: 100%;
      left: -1px;
    }
    &::after {
      right: ${isFirst ? '-1px' : `calc(50% + ${RADIUS})`};
      left: ${isFirst ? `calc(50% + ${RADIUS})` : '-1px'};
      height: 100%;
      border-top: ${BLUE_BORDER};
      border-right: ${isFirst ? BLUE_BORDER : 'none'};
      border-left: ${isFirst ? 'none' : BLUE_BORDER};
    }
  `}
`;

export const Badge = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    min-height: 20px;
    align-items: center;
    gap: ${spaces[2]};
    background: #eadaf2;
    border-radius: 14px;
  `}
`;

export const BadgeContent = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    padding: ${spaces[1]} ${spaces[2]};
    align-items: center;
    gap: ${spaces[1]};
  `}
`;

export const BadgeLabel = styled.div`
  color: #3c0059;
  font: 600 10px / normal Matter;
  letter-spacing: 0.05px;
`;

export const TabsContainer = styled.div`
  transition: background-color 300ms ease;
  border-radius: 8px;
  &:has(div[aria-selected='false']:hover) {
    background-color: #e3e7e9;
  }
`;
